import useResizeObserver from "use-resize-observer";
import {Observer, uplotDefaults} from "web-vcore";
import {Assert, IsNaN} from "js-vextensions";
import {UPlot} from "react-uplot";
import {BaseComponentPlus} from "react-vextensions";
import {useEffect, useMemo, useRef} from "react";
import uPlot from "uplot";
import React from "react";
import {observer_mgl} from "mobx-graphlink";
import {InAndroid} from "../../../../Utils/Bridge/Bridge_Native.js";
import {Belt_samplesPerSecond, BreathProcessor} from "../../../@Shared/Processors/BreathProcessor.js";
import {createRef_withHooks} from "../../../../Utils/UI/ReactHelpers.js";
import {currentBelt} from "../../@Shared/BeltInterface.js";
import {liveSession} from "../../../../Engine/LiveSessionManagement.js";
import {BreathComp} from "../../../../Engine/LiveSession/Components/BreathComp.js";
import {sessionChartColors} from "../../../Timeline/Sessions/SessionUI/SessionChartColors.js";

// is this still needed?
function fixValueFunc(func: (self: uPlot, rawValue: number)=>string) {
	/*return (self: uPlot, rawValue: number)=>{
		if (rawValue == null) return "n/a";
		return func(self, rawValue);
	};*/
	return func;
}

const lineTypes: (uPlot.Series & {key: string})[] = [
	{
		value: fixValueFunc((self, rawValue)=>{
			return `${(rawValue / Belt_samplesPerSecond).toFixed(1)}s`;
		}),
	} as any,
	{
		label: "Force",
		value: fixValueFunc((self, rawValue)=>`${(rawValue ?? 0).toFixed(2)}`),
		stroke: "red",
		points: {show: false},
	},
	{
		label: "Siren",
		value: fixValueFunc((self, rawValue)=>`${(rawValue ?? 0).toFixed(2)}`),
		stroke: "purple",
		points: {show: false},
	},
	{
		label: "Breath peaks",
		value: fixValueFunc((self, rawValue)=>`${(rawValue ?? 0).toFixed(0)}`),
		stroke: sessionChartColors.breath_peak_moreOpaque,
		points: {show: false},
	},
];

/*export let BeltGraph_self: {
	chart: React.RefObject<uPlot>,
	UpdateChart: ()=>void,
}|n;*/
export const BeltGraph = observer_mgl((props: {
	processor: BreathProcessor,
})=>{
	const {processor} = props;
	//const {ref: rootRef, width = 100, height = 100} = useResizeObserver();
	const {callbackRef: rootRef, width = 100, height = 100} = useResizeObserver();

	const self = useMemo(()=>({
		chart: createRef_withHooks<uPlot>({
			postSet: chart=>{
				if (chart == null) return;
				const uplotDiv = chart.root as HTMLDivElement;
				//const doubleClickStartDiv = uplotDiv.querySelector(".u-over") as HTMLDivElement;
				// prevent uplot's double-click-to-reset-zoom behavior
				uplotDiv.addEventListener("dblclick", e=>{
					e.stopPropagation();
				}, {capture: true});

				self.UpdateChart();
			},
		}),
		UpdateChart: ()=>{
			const chart = self.chart.current;
			if (chart == null) return;

			const recentDataLength = processor.samples_force.length.KeepAtMost(1 + (10 * Belt_samplesPerSecond));
			const recentForceSamples = processor.samples_force.slice(-recentDataLength);
			let recentSirenSamples = [] as {value_basic: number, value_forDetailChart: number}[];

			// visualize breath-peaks by setting its line to "1" at the x-position closest to each breath-peak
			const recentBreathPeakSamples = recentForceSamples.map(a=>0);
			for (const peak of processor.breathPeaks) {
				const sampleIndex = recentForceSamples.findIndex(a=>a.time >= peak.sample.time);
				if (sampleIndex < 0) continue; // no sample found
				recentBreathPeakSamples[sampleIndex] = 10;
			}

			const session = liveSession?.AsLocal;
			const liveBreathComp = session?.Comp(BreathComp);
			if (session && liveBreathComp) {
				processor.CalculateSirenStarts(session.coreData.events);
				processor.CalculateSirenSamples();
				recentSirenSamples = processor.samples_siren.slice(-recentDataLength);
			}

			let recentData = [
				recentForceSamples.map(a=>a.value).map((_, i)=>-i.Distance(recentDataLength - 1)),
				recentForceSamples.map(a=>a.value),
				recentSirenSamples.map(a=>a.value_basic * 10),
				recentBreathPeakSamples,
			] as uPlot.AlignedData;

			// temp-fix for error (where data is set to empty arrays, but then somehow the "value" funcs in lineTypes receive null values)
			if (recentData[0].length <= 1) recentData = emptyData() as any;

			//const xScale_old = {min: chart.scales.x.min ?? 0, max: chart.scales.x.max ?? 50};
			chart.setData(recentData, true);

			// calling setScale triggers a chart redraw
			/*chart.batch(()=>{
				Assert([xScale_old.min, xScale_old.max].every(a=>!IsNaN(a)), "Found NaN in new scale values. (GyroChart.tsx)");
				chart.setScale("x", xScale_old);
				//chart.setScale("y", yScale_old);
			});*/
		},
	}), [processor]);
	//BeltGraph_self = self;

	useEffect(()=>{
		const forceListener = (sample: number)=>{
			// todo: probably update this to assign the times more consistently (eg. by [generally] adding to last sample's time, based on the known sample interval of 10/s)
			processor.AddBeltForceSample(sample, Date.now());
			processor.UpdateBreathPeaks_IfXSamplesSinceLastUpdate(5);
			self.UpdateChart();
		};
		currentBelt.forceListeners.push(forceListener);
		return ()=>{
			currentBelt.forceListeners.Remove(forceListener);
			//BeltGraph_self = null; // clear self, so that it doesn't hold onto the chart ref
		};
	});

	//const mounted = useRef(true);

	const legendHeight = 33; // from dev-tools
	const chartOptions: uPlot.Options = useMemo(()=>{
		return {
			id: `chart_live`,
			class: "belt-force-chart",
			width,
			height: height - legendHeight,
			cursor: {
				drag: {x: false, setScale: false},
			},
			axes: [
				{
					label: "Time",
					...uplotDefaults.axis_props_base,
					size: 50,
					values: (u, vals)=>vals.map(indexOffset=>`${(indexOffset / Belt_samplesPerSecond).toFixed(1)}s`),
				},
				{
					label: "Value",
					...uplotDefaults.axis_props_base,
					size: 50,
				},
			],
			scales: {
				x: {
					time: false,
					range(u, dataMin, dataMax): [number, number] {
						return [-10 * Belt_samplesPerSecond, 0];
					},
				},
				y: {
					auto: false,
					//min: -100, max: 100,
					range: (u, dataMin, dataMax)=>{ // use func, since stable
						return [-50, 50] as [number, number]; // -1, just so line shows for sequence of 0s
					},
				},
			},
			series: lineTypes,
		};
	}, [width, height]);
	const data = emptyData(); // start data as empty; populate using UpdateChart()

	return (
		<div ref={rootRef as any} style={{position: "relative", width: "100%", height: "calc(100% - 18px)"}}>
			<UPlot chartRef={self.chart} options={chartOptions} data={data as any}/>
		</div>
	);
});

const emptyData = ()=>[[], [], []];